import { useEffect, useMemo, useState } from "react"
import { Button, Form, Modal } from "react-bootstrap"
import { useVrboQueryMetadataQuery } from "../../../services/bClientApi"
import { ProviderSettingFieldType } from "../../../Types/amenityTypes"
import QuickSelectModal, { QuickSelectOption } from "../QuickSelectModal/QuickSelectModal"

interface VRBOAmenityFieldMeta {
    key: string
    required: boolean
    type: string
    typeDetails?: {
        multipleValuesAllowed?: boolean
        enumTypeValues?: string[]
        feeTypeValues?: string[]
    } | null
}

interface VRBOAmenityMeta {
    key: string
    fieldsRequired: boolean
    value: { fields: VRBOAmenityFieldMeta[] }
}

function buildInitialFieldValues(initialValues?: { fields?: ProviderSettingFieldType[] }): Record<string, any> {
    const result: Record<string, any> = {}
    const storedFields = initialValues?.fields || []
    storedFields.forEach((field) => {
        result[field.key] = field.value
    })
    return result
}

// Renders the input for a single Vrbo amenity field, based on its metadata type
function VRBOAmenityFieldInput(props: {
    fieldMeta: VRBOAmenityFieldMeta
    value: any
    onChange: (value: any) => void
}) {
    const { fieldMeta, value, onChange } = props
    const { key, type, required, typeDetails } = fieldMeta
    const label = `${key}${required ? ' *' : ''}`

    if (type === 'trilean') {
        return (
            <Form.Group className="mb-3">
                <Form.Label>{label}</Form.Label>
                <Form.Select value={value || ''} onChange={(e) => onChange(e.target.value || undefined)}>
                    <option value="">- Not Set -</option>
                    <option value="true">True</option>
                    <option value="false">False</option>
                </Form.Select>
            </Form.Group>
        )
    }

    if (type === 'enum') {
        const enumValues = typeDetails?.enumTypeValues || []
        const selected: string[] = Array.isArray(value) ? value : (value ? [value] : [])
        return (
            <Form.Group className="mb-3">
                <Form.Label>{label}</Form.Label>
                <div>
                    {enumValues.map((enumValue) => (
                        <Form.Check
                            key={enumValue}
                            inline
                            type="checkbox"
                            label={enumValue}
                            checked={selected.includes(enumValue)}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    onChange([...selected, enumValue])
                                } else {
                                    onChange(selected.filter((v) => v !== enumValue))
                                }
                            }}
                        />
                    ))}
                </div>
            </Form.Group>
        )
    }

    if (type === 'fee') {
        const feeValues = typeDetails?.feeTypeValues || []
        return (
            <Form.Group className="mb-3">
                <Form.Label>{label}</Form.Label>
                <Form.Select value={value || ''} onChange={(e) => onChange(e.target.value || undefined)}>
                    <option value="">- Select Fee Type -</option>
                    {feeValues.map((feeValue) => (
                        <option key={feeValue} value={feeValue}>{feeValue}</option>
                    ))}
                </Form.Select>
            </Form.Group>
        )
    }

    if (type === 'text') {
        return (
            <Form.Group className="mb-3">
                <Form.Label>{label}</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={2}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            </Form.Group>
        )
    }

    // string, int, decimal, measurement fallback to plain text input
    return (
        <Form.Group className="mb-3">
            <Form.Label>{label}</Form.Label>
            <Form.Control
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
            />
        </Form.Group>
    )
}

export default function VRBOAmenityMappingModal(props: {
    active: boolean
    level: 'property' | 'unit'
    initialKey?: string
    initialAvailable?: boolean
    initialValues?: { fields?: ProviderSettingFieldType[] }
    onClose: () => void
    onSave: (result: { key: string, available: boolean, values: { fields: ProviderSettingFieldType[] } }) => void
}) {
    const {
        active,
        level,
        initialKey = '',
        initialAvailable = true,
        initialValues,
        onClose,
        onSave,
    } = props

    const metadataType = level === 'unit' ? 'unit-amenities' : 'prop-amenities'
    const { data, isLoading } = useVrboQueryMetadataQuery(metadataType, { skip: !active })

    const amenityMetaList: VRBOAmenityMeta[] = useMemo(() => {
        if (!data?.response) return []
        if (level === 'unit') {
            return data.response?.metadata?.property?.units?.amenities || []
        }
        return data.response?.metadata?.property?.amenities || []
    }, [data, level])

    const [selectedMeta, setSelectedMeta] = useState<VRBOAmenityMeta | null>(null)
    const [available, setAvailable] = useState(initialAvailable)
    const [fieldValues, setFieldValues] = useState<Record<string, any>>({})

    // preselect the amenity we're already mapped to once metadata loads, so the user can edit it
    useEffect(() => {
        if (active && initialKey && amenityMetaList.length > 0 && !selectedMeta) {
            const found = amenityMetaList.find(m => m.key === initialKey)
            if (found) {
                setSelectedMeta(found)
                setAvailable(initialAvailable)
                setFieldValues(buildInitialFieldValues(initialValues))
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, initialKey, amenityMetaList])

    if (!active) return null

    const handleClose = () => {
        setSelectedMeta(null)
        setFieldValues({})
        onClose()
    }

    const handleSelect = (selected: string | string[]) => {
        const key = Array.isArray(selected) ? selected[0] : selected
        const found = amenityMetaList.find(m => m.key === key)
        if (found) {
            setSelectedMeta(found)
            const isSameAsInitial = key === initialKey
            setAvailable(isSameAsInitial ? initialAvailable : true)
            setFieldValues(isSameAsInitial ? buildInitialFieldValues(initialValues) : {})
        }
    }

    const handleSave = () => {
        if (!selectedMeta) return
        const fields: ProviderSettingFieldType[] = (selectedMeta.value?.fields || [])
            .map((fieldMeta) => ({
                key: fieldMeta.key,
                type: fieldMeta.type,
                value: fieldValues[fieldMeta.key],
            }))
            .filter((f) => f.value !== undefined && f.value !== '' && !(Array.isArray(f.value) && f.value.length === 0))

        onSave({
            key: selectedMeta.key,
            available,
            values: { fields },
        })
        setSelectedMeta(null)
        setFieldValues({})
    }

    if (!selectedMeta) {
        const options: QuickSelectOption[] = amenityMetaList.map((meta) => ({
            id: meta.key,
            label: meta.key,
            secondaryLabel: meta.fieldsRequired ? 'fields required' : undefined,
        }))

        return (
            <QuickSelectModal
                active={active}
                onClose={handleClose}
                onSelect={handleSelect}
                options={options}
                title={`Select Vrbo ${level === 'unit' ? 'Unit' : 'Property'} Amenity`}
                message="Search and select the Vrbo amenity to map this amenity to"
                isLoading={isLoading}
                filterPlaceholder="Filter Vrbo Amenities"
            />
        )
    }

    const fieldsMeta = selectedMeta.value?.fields || []

    return (
        <Modal show={active} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Configure Vrbo Amenity: {selectedMeta.key}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Check
                    className="mb-3"
                    type="checkbox"
                    label="Available"
                    checked={available}
                    onChange={(e) => setAvailable(e.target.checked)}
                />

                {fieldsMeta.length === 0 && (
                    <p className="text-muted">This amenity has no additional fields to configure.</p>
                )}

                {fieldsMeta.map((fieldMeta) => (
                    <VRBOAmenityFieldInput
                        key={fieldMeta.key}
                        fieldMeta={fieldMeta}
                        value={fieldValues[fieldMeta.key]}
                        onChange={(value) => setFieldValues((prev) => ({ ...prev, [fieldMeta.key]: value }))}
                    />
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={handleSave}>Save Mapping</Button>
                <Button variant="secondary" onClick={() => setSelectedMeta(null)}>Back</Button>
                <Button variant="danger" onClick={handleClose}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    )
}
